import { useState } from "react";
import { useLocation } from "wouter";
import { AuthShell, Field, SubmitButton } from "./AuthShell";
import { useCaptcha } from "./useCaptcha";
import { t } from "../../locale";
import { useAuth } from "../../stores/auth";
import { useGame, selectMe } from "../../stores/game";

/**
 * plan.md §5: an account restores a saved display name and mark, and nothing
 * else. Playing never requires one, which is why this screen is only ever
 * reached from a footer link and never stands between a player and a room.
 */
export const Register = () => {
  const [, navigate] = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const captcha = useCaptcha(setError);
  const register = useAuth((s) => s.register);
  const updateProfile = useAuth((s) => s.updateProfile);
  const session = useAuth((s) => s.session);
  const me = useGame(selectMe);

  const ready =
    email.trim().length > 0 &&
    password.length > 0 &&
    (captcha.token !== null || captcha.bypassed);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const linking = session?.user.is_anonymous === true;
      await register(email.trim(), password, captcha.token ?? undefined);

      /*
       * plan.md §5: an account restores a saved display name and mark. For a
       * guest upgrading in place that is the whole point — they already have
       * both, and are the player most likely to want them kept. Nothing was
       * being saved before, so the promise was empty for exactly this person.
       *
       * Best effort: the account exists either way, and a failed profile write
       * must not read as a failed registration.
       */
      if (linking && me) {
        await updateProfile({
          display_name: me.display_name,
          avatar_seed: me.avatar_seed,
        }).catch(() => {});
      }

      navigate("/auth/confirm", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t.auth.genericFailure);
      captcha.reset();
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title={t.auth.register} error={error} allowInRoom>
      <Field
        label={t.auth.email}
        type="email"
        value={email}
        onChange={setEmail}
        autoComplete="email"
      />
      <Field
        label={t.auth.password}
        type="password"
        value={password}
        onChange={setPassword}
        autoComplete="new-password"
      />
      {captcha.widget}
      <SubmitButton
        label={t.auth.register}
        disabled={!ready || busy}
        onClick={submit}
      />
      <button
        className="min-h-0 px-2 py-2 text-sm text-ink-dim underline"
        onClick={() => navigate("/auth/sign-in")}
      >
        {t.auth.haveAccount}
      </button>
      {/* Said at the point the email is actually collected, not only on a page
          the player would have to go looking for. */}
      <p className="px-2 text-xs leading-relaxed text-ink-dim">
        {t.privacy.registerNotice}{" "}
        <button
          className="min-h-0 min-w-0 underline underline-offset-2"
          onClick={() => navigate("/privacy")}
        >
          {t.privacy.registerNoticeLink}
        </button>
      </p>
    </AuthShell>
  );
};
